/**
 * Copy of the Cost Services screens (ALC-4474). Spread into the `billing` namespace as the
 * top-level `costServices` block.
 *
 * Knob labels are NOT here: they live once in `@alinea/types` (`COST_SERVICE_KNOB_CATALOG`) and the
 * screens read them in the language of the screen. Error sentences are NOT here either: they live
 * once in `COST_SERVICE_ERROR_MESSAGES` and are rendered from the stable `code`.
 */
export const costServicesPtBr = {
  costServices: {
    title: 'Serviços de custo',
    subtitle:
      'Custos pagos a terceiros que acompanham a receita faturada, como royalties. Cada serviço de custo diz quem recebe, quanto e em quais contas.',
    backToList: 'Voltar para serviços de custo',
    status: {
      DRAFT: 'Rascunho',
      ACTIVE: 'Ativo',
      INACTIVE: 'Inativo',
    },
    baseType: {
      PERCENT_OF_REVENUE: 'Percentual da receita',
      FIXED_PER_CONTRACT: 'Valor fixo por contrato',
      FIXED_PER_MONTH: 'Valor fixo por mês',
    },
    trigger: {
      SCHEDULE: 'Agenda',
      INVOICE_ISSUED: 'Fatura emitida',
      SETTLEMENT_APPROVED: 'Baixa aprovada',
    },
    template: {
      Royalty: {
        name: 'Royalty',
        description:
          'Uma parte da receita (ou um valor fixo por contrato ou por mês) devida a um fornecedor, provisionada junto com a receita.',
      },
      fromTemplate: 'Do modelo {{name}}',
      modified: 'Modelo {{name}} · modificado',
    },
    list: {
      createButton: 'Novo serviço de custo',
      tabs: {
        all: 'Todos',
        DRAFT: 'Rascunhos',
        ACTIVE: 'Ativos',
        INACTIVE: 'Inativos',
      },
      columns: {
        name: 'Nome',
        entity: 'Entidade',
        vendor: 'Fornecedor',
        rate: 'Taxa',
        currency: 'Moeda',
        status: 'Status',
        attachments: 'Serviços de faturamento',
      },
      viewsLabel: 'Status dos serviços de custo',
      searchLabel: 'Buscar serviços de custo',
      searchPlaceholder: 'Buscar por nome, entidade ou fornecedor',
      rowActions: 'Ações de {{name}}',
      open: 'Abrir',
      noVendor: 'Sem fornecedor',
      noRate: 'Sem taxa',
      empty: {
        title: 'Nenhum serviço de custo ainda',
        description:
          'Crie um serviço de custo a partir do modelo Royalty para provisionar o que você paga ao fornecedor junto com a receita.',
      },
    },
    create: {
      title: 'Novo serviço de custo',
      template: { label: 'Modelo', locked: 'Único modelo disponível' },
      entity: { label: 'Entidade', placeholder: 'Selecione a entidade' },
      name: { label: 'Nome', placeholder: 'Ex.: Royalty da marca X' },
      submit: 'Criar rascunho',
      submitting: 'Criando...',
      cancel: 'Cancelar',
      errorTitle: 'O serviço de custo não foi criado',
      invalidTitle: 'Corrija os campos destacados antes de criar.',
      nameRequired: 'Informe o nome do serviço de custo.',
      entityRequired: 'Selecione a entidade.',
    },
    detail: {
      tabs: {
        configuration: 'Configuração',
        attachments: 'Serviços de faturamento',
      },
      loadError: 'Não foi possível carregar este serviço de custo.',
      notFound: 'Serviço de custo não encontrado.',
      retry: 'Tentar de novo',
      readOnly: 'Você pode ver este serviço de custo, mas não alterá-lo.',
      inactiveBanner:
        'Este serviço de custo está inativo. Ele não vale para os serviços de faturamento vinculados até ser ativado de novo.',
      save: 'Salvar alterações',
      saving: 'Salvando...',
      discard: 'Descartar',
      discardDialog: {
        title: 'Descartar as alterações?',
        description: 'As alterações não gravadas deste serviço de custo serão perdidas.',
        confirm: 'Descartar alterações',
      },
      saved: 'Alterações gravadas.',
      saveErrorTitle: 'As alterações não foram gravadas',
      invalidTitle: 'Corrija os campos destacados antes de salvar.',
      unsavedHint: 'Há alterações não gravadas.',
      activate: 'Ativar',
      deactivate: 'Desativar',
      activateDialog: {
        title: 'Ativar "{{name}}"?',
        description:
          'Antes de ativar, conferimos fornecedor, taxa, contas e período. O serviço passa a valer para os serviços de faturamento vinculados.',
        confirm: 'Ativar',
        success: 'Serviço de custo ativado.',
        missingTitle: 'Faltam as contas:',
        missingAccounts: 'Defina {{list}} antes de ativar.',
        accountNames: {
          costAccount: 'a conta de custo',
          liabilityAccount: 'a conta de passivo',
          varianceAccount: 'a conta de variação',
        },
      },
      deactivateDialog: {
        title: 'Desativar "{{name}}"?',
        description:
          'O serviço deixa de valer para os serviços de faturamento vinculados. Você pode ativá-lo de novo depois.',
        confirm: 'Desativar',
        success: 'Serviço de custo desativado.',
      },
    },
    form: {
      required: 'Obrigatório',
      stepper: {
        previous: 'Anterior',
        next: 'Próximo',
        position: 'Etapa {{current}} de {{total}}',
        invalid: 'Esta etapa tem campos a corrigir.',
      },
      requiredToActivate: 'Obrigatório para ativar. O rascunho salva sem este campo.',
      help: 'Ajuda: {{label}}',
      resetToTemplate: 'Restaurar valor do template',
      resetToTemplateNamed: 'Restaurar o valor do template em {{label}}',
      sections: {
        identification: {
          title: 'Identificação',
          description: 'O modelo de partida, a entidade e o nome do serviço de custo.',
        },
        vendor: {
          title: 'Fornecedor',
          description: 'Quem recebe o custo.',
        },
        calculation: {
          title: 'Cálculo do custo',
          description: 'Como o valor de cada provisão é calculado.',
        },
        accounting: {
          title: 'Contabilização',
          description: 'As contas dos lançamentos de provisão e de baixa.',
        },
        window: {
          title: 'Vigência e gatilhos',
          description: 'Quando a receita gera provisão deste custo.',
        },
        settlement: {
          title: 'Baixa da provisão',
          description: 'Como a conta a pagar do fornecedor baixa as provisões.',
        },
      },
      tooltips: {
        template:
          'O modelo preenche os valores de partida. Os campos com o ícone de raio ainda têm o valor do modelo; qualquer um pode ser mudado.',
        entity:
          'A entidade em cujos livros as provisões são lançadas. As contas contábeis abaixo vêm do plano de contas dela.',
        name: 'Como este serviço de custo aparece nas listas, nas provisões e nas contas a pagar. Ex.: Royalty da marca X.',
        baseType:
          'Como o custo é calculado: um percentual da receita dos serviços de faturamento vinculados, um valor fixo por contrato (cobrado uma vez) ou um valor fixo por mês (cobrado em todo mês com receita, para cada item do contrato).',
        rate: {
          PERCENT_OF_REVENUE:
            'Percentual aplicado à receita de cada serviço de faturamento vinculado. Ex.: 12,5. Um vínculo com taxa específica usa a dele no lugar desta.',
          FIXED_PER_CONTRACT:
            'Valor fixo por contrato, na moeda do fornecedor (o campo antes deste). Ex.: 1.200,00. Um vínculo com taxa específica usa a dele no lugar desta.',
          FIXED_PER_MONTH:
            'Valor fixo por mês, na moeda do fornecedor (o campo antes deste), cobrado para cada item do contrato em cada mês com receita. Ex.: US$ 100,00 por mês por item do contrato, seja qual for o preço do contrato. Um vínculo com taxa específica usa a dele no lugar desta.',
        },
        fixedRecognitionMonths:
          'De uma vez: o valor fixo inteiro é provisionado no mês da primeira receita do contrato. Distribuir: o valor é dividido em parcelas mensais iguais a partir desse mês; a última parcela leva o arredondamento. Depois do primeiro lançamento não dá para mudar.',
        chargeFixedOnRenewal:
          'Desligado (padrão): o valor fixo por contrato é cobrado uma vez na vida do contrato. Ligado: é cobrado de novo a cada renovação, na primeira receita do novo prazo (com "Distribuir em N meses", cada prazo começa um novo plano). Ex.: US$ 1.200,00 por contrato, renovado em 01/01/2027: cobra US$ 1.200,00 em janeiro de 2026 e de novo em janeiro de 2027. Só vale para "Valor fixo por contrato": o "Valor fixo por mês" já é cobrado em todo mês com receita, também depois da renovação.',
        vendor:
          'Quem recebe este custo. As contas a pagar deste fornecedor são as que baixam as provisões deste serviço.',
        autoMarkVendorBills:
          'Ligado: toda conta a pagar deste fornecedor chega marcada como "Serviço de custo", sem precisar de regra de intake. Precisa de um fornecedor escolhido.',
        currency: {
          PERCENT_OF_REVENUE:
            'A moeda em que o fornecedor manda a conta a pagar. Não muda o percentual: ele vale sobre a receita do serviço de faturamento, na moeda dessa receita. A provisão é convertida para esta moeda pela cotação do dia, para casar com a conta a pagar e ser reavaliada no fim do mês. Ex.: 40% de R$ 10.000,00 = R$ 4.000,00 → US$ 800,00 com cotação 5,00. Fornecedor que cobra em reais: deixe Real. É também a moeda do valor de tolerância.',
          FIXED_PER_CONTRACT:
            'A moeda em que o fornecedor cobra. O valor fixo ao lado é escrito nesta moeda; a provisão é lançada na moeda funcional da entidade pela cotação do dia. É também a moeda do valor de tolerância.',
          FIXED_PER_MONTH:
            'A moeda em que o fornecedor cobra. O valor fixo por mês ao lado é escrito nesta moeda; a provisão de cada mês é lançada na moeda funcional da entidade pela cotação do dia. É também a moeda do valor de tolerância.',
        },
        costAccount:
          'Débito do custo reconhecido no mês: cada provisão lança D esta conta / C a conta de passivo. Ao encerrar uma provisão que não virá, o estorno credita esta conta. A linha do DRE segue o mapeamento desta conta em FP&A > Rollups. Sem ela, as provisões ficam aguardando lançamento.',
        liabilityAccount:
          'Crédito da provisão: cada provisão lança C esta conta. Na aprovação da conta a pagar do fornecedor, a baixa debita esta conta contra Fornecedores. A parte paga antes do mês vai a débito de Despesas antecipadas (conta do sistema AP_COST_SERVICE_PREPAID) e é reclassificada contra esta conta quando a provisão do mês nasce. Não pode ser a conta de controle de fornecedores. Sem ela, as provisões ficam aguardando lançamento.',
        varianceAccount:
          'Diferença na baixa: quando a conta a pagar não bate com a provisão baixada, a diferença vai para esta conta (débito se a conta a pagar veio maior, crédito se veio menor). A variação cambial não passa por aqui. Em branco, a aprovação de uma conta a pagar com diferença é recusada.',
        activeFrom:
          'Primeiro dia em que a receita gera provisão deste custo. Em branco, vale desde sempre.',
        activeTo:
          'Último dia em que a receita gera provisão deste custo. Em branco, não tem fim. Não pode ser antes do início.',
        triggers:
          'A provisão nasce da receita: cada gatilho é um momento em que a receita é lançada. Agenda: o reconhecimento mensal de um item de contrato com cronograma. Fatura emitida: a receita reconhecida na emissão da fatura (itens sem cronograma). Baixa aprovada: a receita reconhecida quando a baixa é aprovada (regime de caixa ou adquirente). O lançamento é o mesmo nos três (D conta de custo / C conta de provisão); a receita lança uma vez, então o custo também. Desmarcar um gatilho deixa sem provisão a receita reconhecida naquele momento. Para ativar, marque pelo menos um.',
        overrides:
          'Quantos serviços de faturamento vinculados usam uma taxa própria no lugar da taxa deste serviço. Mude na aba "Serviços de faturamento".',
        tolerance:
          'Diferença aceita entre a linha da conta a pagar e a provisão sem pedir confirmação. Exata: qualquer diferença pede revisão. Valor: até esse valor, na moeda do serviço (a mesma da provisão e da conta a pagar). Percentual: até esse percentual do valor da linha. A diferença vai para a conta de variação.',
        autoSelectEntriesWithinTolerance:
          'Ligado: quando a conta a pagar chega dentro da tolerância, as provisões que ela paga já vêm selecionadas para a baixa. Desligado: elas são só sugeridas.',
        warnOnExpenseWithOpenProvisions:
          'Ligado: aprovar uma conta a pagar deste fornecedor com linha de despesa enquanto há provisões em aberto registra um aviso no histórico. Nunca bloqueia a aprovação.',
        staleAfterDays:
          'Dias depois da data esperada de pagamento em que uma provisão ainda aberta passa a aparecer no envelhecimento. Em branco, nunca envelhece.',
      },
    },
    knob: {
      rateLabel: {
        PERCENT_OF_REVENUE: 'Taxa do custo (% da receita)',
        FIXED_PER_CONTRACT: 'Valor do custo por contrato',
        FIXED_PER_MONTH: 'Valor do custo por mês',
      },
      // A percent is not written in a currency: the field only says how the vendor bills (owner, 10-11).
      currencyLabel: {
        PERCENT_OF_REVENUE: 'Moeda da fatura do fornecedor',
        FIXED_PER_CONTRACT: 'Moeda do fornecedor',
        FIXED_PER_MONTH: 'Moeda do fornecedor',
      },
      chargeFixedOnRenewalLabel: 'Cobrar de novo na renovação',
      derived: 'Calculado a partir dos vínculos',
      overridesSummary: 'Vínculos com taxa específica: {{count}}.',
      // Under the rate of a link (link-terms-fields): the service form says it in the tooltip.
      rateHint: {
        PERCENT_OF_REVENUE: 'Percentual da receita faturada. Ex.: 12,5 ou 12,5%',
        FIXED_PER_CONTRACT: 'Valor fixo por contrato, na moeda do fornecedor. Ex.: 1.200,00',
        FIXED_PER_MONTH:
          'Valor fixo por mês, por item do contrato, na moeda do fornecedor. Ex.: 100,00',
      },
      ratePlaceholder: {
        PERCENT_OF_REVENUE: '12,5%',
        FIXED_PER_CONTRACT: '1.200,00',
        FIXED_PER_MONTH: '100,00',
      },
      triggersHint:
        'Todos os gatilhos fazem o mesmo lançamento (D conta de custo / C conta de provisão), só em momentos diferentes. Desmarcar um deixa sem provisão a receita reconhecida naquele momento.',
      tolerance: {
        mode: {
          EXACT: 'Exata',
          AMOUNT: 'Valor',
          PERCENT: 'Percentual',
        },
        amountLabel: 'Valor da tolerância ({{currency}})',
        percentLabel: 'Percentual da tolerância',
      },
      fixedRecognition: {
        atOnce: 'De uma vez',
        spread: 'Distribuir em N meses',
        monthsLabel: 'Número de meses',
        monthsSuffix: 'meses (1 a 120)',
      },
      selectEntityFirst: 'Selecione a entidade para escolher as contas.',
      selectAccount: 'Selecione a conta',
      selectVendor: 'Selecione o fornecedor',
      selectCurrency: 'Selecione a moeda',
      selectBaseType: 'Selecione a base do custo',
      rateLockedHint_one:
        'A taxa não pode mudar aqui: {{count}} vínculo que usa esta taxa já tem provisões lançadas ou pendentes. Mude a taxa no vínculo ("Editar" em Serviços de faturamento) e informe a partir de quando ela vale.',
      rateLockedHint_other:
        'A taxa não pode mudar aqui: {{count}} vínculos que usam esta taxa já têm provisões lançadas ou pendentes. Mude a taxa em cada vínculo ("Editar" em Serviços de faturamento) e informe a partir de quando ela vale.',
      baseTypeLockedHint_one:
        'A base não pode mudar enquanto {{count}} vínculo vigente tiver taxa específica. Encerre esse vínculo para trocar a base.',
      baseTypeLockedHint_other:
        'A base não pode mudar enquanto {{count}} vínculos vigentes tiverem taxa específica. Encerre esses vínculos para trocar a base.',
    },
    // "Resumo do custo": the sidebar of the form. The fixed-base wording lives in `fixed` (per
    // contract, D22/D40) and `monthly` (per month, D39).
    summary: {
      title: 'Resumo do custo',
      fields: {
        base: 'Base',
        rate: 'Taxa',
        amount: 'Valor',
        currency: 'Moeda',
        vendor: 'Fornecedor',
        triggers: 'Gatilhos',
        tolerance: 'Tolerância',
      },
      values: {
        notDefined: 'Não definido',
        vendorChosen: 'Fornecedor escolhido',
        noTriggers: 'Nenhum',
        noTriggersShort: 'nenhum gatilho',
        toleranceExact: 'Exata',
      },
      accounts: {
        cost: 'Conta de custo',
        liability: 'Conta de provisão',
        variance: 'Conta de variação',
      },
      example: {
        title: 'Exemplo',
        percent: 'Receita de {{revenue}} → provisão de {{provision}}',
        percentConverted:
          'Receita de {{revenue}} → provisão de {{provision}}, convertida para {{currency}} pela cotação do dia',
        needRate: 'Informe a taxa para ver um exemplo.',
      },
      fixed: {
        field: 'Reconhecimento do valor fixo',
        atOnce: 'De uma vez',
        spread_one: 'Em {{count}} mês',
        spread_other: 'Em {{count}} meses',
        spreadPending: 'Em parcelas mensais (informe os meses)',
        exampleAtOnce: '1ª receita do contrato → provisão de {{amount}}',
        exampleSpread_one: '1ª receita do contrato → {{amount}} por mês durante {{count}} mês',
        exampleSpread_other: '1ª receita do contrato → {{amount}} por mês durante {{count}} meses',
        exampleSpreadRounded_one:
          '1ª receita do contrato → {{amount}} por mês durante {{count}} mês; a última parcela leva o arredondamento',
        exampleSpreadRounded_other:
          '1ª receita do contrato → {{amount}} por mês durante {{count}} meses; a última parcela leva o arredondamento',
        examplePending: 'Informe o número de meses para ver o exemplo.',
        note: 'Um valor por contrato, provisionado uma vez.',
        noteRenewal: 'Um valor por contrato, cobrado de novo a cada renovação do contrato.',
      },
      monthly: {
        field: 'Cobrança do valor fixo',
        charge: '{{amount}} por mês por item do contrato',
        example:
          'Cada mês com receita → provisão de {{amount}} por item do contrato, seja qual for o preço do contrato',
        note: 'Um valor por item do contrato em cada mês com receita, também depois da renovação.',
      },
      sequence: {
        title: 'Sequência do custo',
        revenue: 'Receita reconhecida ({{triggers}})',
        revenueDetail: 'Provisão: D {{cost}} / C {{liability}}',
        bill: 'Conta a pagar do fornecedor chega',
        billDetail: 'Baixa a provisão.',
        difference: 'Diferença na baixa',
        differenceDetail: 'Dentro da tolerância: vai para {{variance}}. Fora: fica para revisão.',
        differenceExact: 'Tolerância exata: qualquer diferença fica para revisão.',
      },
    },
    leaveDialog: {
      title: 'Sair sem salvar?',
      description:
        'Há alterações não gravadas neste serviço de custo. Se sair agora, elas serão perdidas.',
      confirm: 'Sair sem salvar',
      stay: 'Continuar editando',
    },
    result: {
      fields: {
        vendor: 'Fornecedor',
        rate: 'Taxa',
        billingServices: 'Serviços de faturamento',
        costService: 'Serviço de custo',
        billingService: 'Serviço de faturamento',
        period: 'Período',
      },
      rate: {
        PERCENT_OF_REVENUE: '{{rate}} da receita',
        FIXED_PER_CONTRACT: '{{amount}} por contrato',
        FIXED_PER_MONTH: '{{amount}} por mês',
      },
      noBillingServices: 'Nenhum',
      activated: {
        nextWithLinks:
          'As provisões começam no próximo reconhecimento de receita desses serviços de faturamento.',
        nextWithoutLinks:
          'Nenhum serviço de faturamento vinculado ainda: vincule um na aba "Serviços de faturamento" para começar a provisionar.',
      },
      deactivated: {
        next: 'O serviço deixa de valer para os serviços de faturamento vinculados. Você pode ativá-lo de novo depois.',
      },
      linked: {
        next: 'O custo passa a ser provisionado quando a receita do serviço de faturamento for reconhecida, enquanto o serviço de custo estiver ativo.',
      },
      edited: {
        next: 'A mudança vale daqui para frente: os custos que este vínculo já reconheceu não são alterados.',
      },
      ended: {
        next: 'O histórico do vínculo é mantido e nada é apagado.',
      },
    },
    links: {
      columns: {
        rate: 'Taxa',
        period: 'Período',
        status: 'Status',
        actions: 'Ações',
      },
      inheritsRate: 'Taxa do serviço',
      overrideRate: 'taxa específica',
      period: '{{from}} – {{to}}',
      openEnded: 'Sem fim',
      noStart: 'Desde o início',
      ended: 'Encerrado',
      edit: 'Editar',
      editNamed: 'Editar: {{name}}',
      end: 'Encerrar',
      endNamed: 'Encerrar: {{name}}',
      open: 'Abrir',
      openNamed: 'Abrir: {{name}}',
      unknownName: 'Serviço removido',
      nameUnavailable: 'Nome do serviço indisponível',
      loadError: 'Não foi possível carregar os vínculos.',
      noPermission: 'Você não tem permissão para ver os serviços de custo.',
      terms: {
        rateOverride: 'Taxa específica (opcional)',
        activeFrom: 'Ativo a partir de',
        activeTo: 'Ativo até',
      },
      addDialog: {
        rateAfterService: 'Escolha o serviço de custo para informar a taxa específica.',
        submit: 'Vincular',
        cancel: 'Cancelar',
      },
      editDialog: {
        title: 'Editar o vínculo com "{{name}}"',
        description:
          'Mude a taxa específica ou o período. A mudança vale daqui para frente: os custos que este vínculo já reconheceu não são alterados.',
        ended:
          'Este vínculo está encerrado. Para reabri-lo, limpe a data final ou escolha uma data futura.',
        confirm: 'Salvar vínculo',
        cancel: 'Cancelar',
        unchanged: 'Altere a taxa ou o período para salvar.',
        success: 'Vínculo atualizado.',
        effectiveFrom: 'Nova taxa vale a partir de',
        effectiveFromHint:
          'Se o vínculo já provisionou custos, ele termina na véspera desta data e um novo vínculo leva a nova taxa; meses anteriores, mesmo lançados depois, usam a taxa antiga.',
        effectiveFromRequired: 'Informe a partir de quando a nova taxa vale.',
        earliestAfterPosted:
          'Já existem provisões lançadas ou pendentes até {{through}}. Escolha uma data a partir de {{date}}. Para corrigir meses lançados, use estornos e lançamentos manuais.',
        earliestAfterClosed:
          'O período até a véspera de {{date}} está fechado. Escolha uma data a partir de {{date}}.',
        earliestUnavailable:
          'Não foi possível ler a data mínima da nova taxa. Feche e abra o diálogo de novo.',
      },
      endDialog: {
        title: 'Encerrar o vínculo com "{{name}}"?',
        description:
          'O vínculo deixa de valer a partir da data abaixo. O histórico é mantido e nada é apagado.',
        endDate: 'Último dia do vínculo',
        confirm: 'Encerrar vínculo',
        success: 'Vínculo encerrado.',
      },
      costService: {
        title: 'Serviços de faturamento vinculados',
        description:
          'Cada vínculo diz a quais serviços de faturamento este custo acompanha, com taxa específica e período próprios, se houver.',
        columnName: 'Serviço de faturamento',
        empty: {
          title: 'Nenhum serviço de faturamento vinculado',
          description:
            'Vincule um serviço de faturamento para que este custo acompanhe a receita dele.',
        },
        add: 'Vincular serviço',
        loadNamesError: 'Não foi possível carregar os serviços de faturamento desta entidade.',
        addDialog: {
          title: 'Vincular serviço de faturamento',
          description: 'Só aparecem serviços de faturamento ativos da mesma entidade deste custo.',
          service: 'Serviço de faturamento',
          servicePlaceholder: 'Selecione o serviço de faturamento',
          noServices: 'Esta entidade não tem serviços de faturamento ativos.',
          serviceRequired: 'Selecione o serviço de faturamento.',
          success: 'Serviço de faturamento vinculado.',
        },
      },
      billingService: {
        title: 'Custos de serviço',
        description:
          'Serviços de custo que acompanham este serviço de faturamento, com taxa específica e período próprios, se houver.',
        columnName: 'Serviço de custo',
        empty: {
          title: 'Nenhum serviço de custo vinculado',
          description:
            'Vincule um serviço de custo para que ele acompanhe a receita deste serviço de faturamento.',
        },
        add: 'Vincular custo',
        loadNamesError: 'Não foi possível carregar os serviços de custo desta entidade.',
        addDialog: {
          title: 'Vincular serviço de custo',
          description:
            'Só aparecem serviços de custo da mesma entidade deste serviço de faturamento, sem os inativos.',
          service: 'Serviço de custo',
          servicePlaceholder: 'Selecione o serviço de custo',
          noServices: 'Esta entidade não tem serviços de custo para vincular.',
          serviceRequired: 'Selecione o serviço de custo.',
          success: 'Serviço de custo vinculado.',
        },
      },
    },
    errors: {
      generic: 'Não foi possível concluir a operação. Confira os dados e tente de novo.',
    },
  },
};

export const costServicesEn = {
  costServices: {
    title: 'Cost services',
    subtitle:
      'Costs paid to third parties that follow the billed revenue, such as royalties. Each cost service says who gets paid, how much and in which accounts.',
    backToList: 'Back to cost services',
    status: {
      DRAFT: 'Draft',
      ACTIVE: 'Active',
      INACTIVE: 'Inactive',
    },
    baseType: {
      PERCENT_OF_REVENUE: 'Percent of revenue',
      FIXED_PER_CONTRACT: 'Fixed per contract',
      FIXED_PER_MONTH: 'Fixed per month',
    },
    trigger: {
      SCHEDULE: 'Schedule',
      INVOICE_ISSUED: 'Invoice issued',
      SETTLEMENT_APPROVED: 'Settlement approved',
    },
    template: {
      Royalty: {
        name: 'Royalty',
        description:
          'A share of revenue (or a fixed amount per contract or per month) owed to a vendor, provisioned together with the revenue.',
      },
      fromTemplate: 'From the {{name}} template',
      modified: '{{name}} template · modified',
    },
    list: {
      createButton: 'New cost service',
      tabs: {
        all: 'All',
        DRAFT: 'Drafts',
        ACTIVE: 'Active',
        INACTIVE: 'Inactive',
      },
      columns: {
        name: 'Name',
        entity: 'Entity',
        vendor: 'Vendor',
        rate: 'Rate',
        currency: 'Currency',
        status: 'Status',
        attachments: 'Billing services',
      },
      viewsLabel: 'Cost service statuses',
      searchLabel: 'Search cost services',
      searchPlaceholder: 'Search by name, entity or vendor',
      rowActions: 'Actions for {{name}}',
      open: 'Open',
      noVendor: 'No vendor',
      noRate: 'No rate',
      empty: {
        title: 'No cost services yet',
        description:
          'Create a cost service from the Royalty template to provision what you pay the vendor together with the revenue.',
      },
    },
    create: {
      title: 'New cost service',
      template: { label: 'Template', locked: 'Only template available' },
      entity: { label: 'Entity', placeholder: 'Select the entity' },
      name: { label: 'Name', placeholder: 'E.g. Brand X royalty' },
      submit: 'Create draft',
      submitting: 'Creating...',
      cancel: 'Cancel',
      errorTitle: 'The cost service was not created',
      invalidTitle: 'Fix the highlighted fields before creating.',
      nameRequired: 'Enter the name of the cost service.',
      entityRequired: 'Select the entity.',
    },
    detail: {
      tabs: {
        configuration: 'Configuration',
        attachments: 'Billing services',
      },
      loadError: 'Could not load this cost service.',
      notFound: 'Cost service not found.',
      retry: 'Try again',
      readOnly: 'You can view this cost service but not change it.',
      inactiveBanner:
        'This cost service is inactive. It does not apply to the linked billing services until it is activated again.',
      save: 'Save changes',
      saving: 'Saving...',
      discard: 'Discard',
      discardDialog: {
        title: 'Discard the changes?',
        description: 'The unsaved changes of this cost service will be lost.',
        confirm: 'Discard changes',
      },
      saved: 'Changes saved.',
      saveErrorTitle: 'The changes were not saved',
      invalidTitle: 'Fix the highlighted fields before saving.',
      unsavedHint: 'You have unsaved changes.',
      activate: 'Activate',
      deactivate: 'Deactivate',
      activateDialog: {
        title: 'Activate "{{name}}"?',
        description:
          'Before activating, we check the vendor, rate, accounts and period. The service then applies to the linked billing services.',
        confirm: 'Activate',
        success: 'Cost service activated.',
        missingTitle: 'Missing accounts:',
        missingAccounts: 'Set {{list}} before activating.',
        accountNames: {
          costAccount: 'the cost account',
          liabilityAccount: 'the liability account',
          varianceAccount: 'the variance account',
        },
      },
      deactivateDialog: {
        title: 'Deactivate "{{name}}"?',
        description:
          'The service stops applying to the linked billing services. You can activate it again later.',
        confirm: 'Deactivate',
        success: 'Cost service deactivated.',
      },
    },
    form: {
      required: 'Required',
      stepper: {
        previous: 'Previous',
        next: 'Next',
        position: 'Step {{current}} of {{total}}',
        invalid: 'This step has fields to fix.',
      },
      requiredToActivate: 'Required to activate. A draft saves without this field.',
      help: 'Help: {{label}}',
      resetToTemplate: 'Restore template value',
      resetToTemplateNamed: 'Restore the template value of {{label}}',
      sections: {
        identification: {
          title: 'Identification',
          description: 'The starting template, the entity and the name of the cost service.',
        },
        vendor: {
          title: 'Vendor',
          description: 'Who gets paid.',
        },
        calculation: {
          title: 'Cost calculation',
          description: 'How the amount of each provision is calculated.',
        },
        accounting: {
          title: 'Accounting',
          description: 'The accounts of the provision and relief entries.',
        },
        window: {
          title: 'Period and triggers',
          description: 'When revenue creates a provision of this cost.',
        },
        settlement: {
          title: 'Provision relief',
          description: "How the vendor's bill relieves the provisions.",
        },
      },
      tooltips: {
        template:
          'The template fills in the starting values. Fields with the lightning icon still hold the template value; any of them can change.',
        entity:
          'The entity whose books receive the provisions. The GL accounts below come from its chart of accounts.',
        name: 'How this cost service shows in lists, provisions and bills. E.g. Brand X royalty.',
        baseType:
          'How the cost is calculated: a percent of the revenue of the linked billing services, a fixed amount per contract (charged once) or a fixed amount per month (charged in every month with revenue, for each contract item).',
        rate: {
          PERCENT_OF_REVENUE:
            'Percent applied to the revenue of each linked billing service. E.g. 12.5. A link with its own rate uses it instead of this one.',
          FIXED_PER_CONTRACT:
            'Fixed amount per contract, in the vendor currency (the field before this one). E.g. 1,200.00. A link with its own rate uses it instead of this one.',
          FIXED_PER_MONTH:
            'Fixed amount per month, in the vendor currency (the field before this one), charged for each contract item in every month with revenue. E.g. US$ 100.00 a month per contract item, whatever the contract price. A link with its own rate uses it instead of this one.',
        },
        fixedRecognitionMonths:
          'At once: the whole fixed amount is provisioned in the month of the first revenue of the contract. Spread: the amount is split into equal monthly parts from that month; the last part takes the rounding. It cannot change after the first posting.',
        chargeFixedOnRenewal:
          'Off (default): the fixed amount per contract is charged once in the life of the contract. On: it is charged again on each renewal, on the first revenue of the new term (with "Spread over N months", each term starts a new plan). E.g. US$ 1,200.00 per contract, renewed on 01/01/2027: charges US$ 1,200.00 in January 2026 and again in January 2027. It only applies to "Fixed per contract": "Fixed per month" is already charged in every month with revenue, also after the renewal.',
        vendor:
          "Who gets paid this cost. This vendor's bills are the ones that relieve the provisions of this service.",
        autoMarkVendorBills:
          'On: every bill from this vendor arrives marked as "Cost service", with no intake rule needed. Needs a vendor.',
        currency: {
          PERCENT_OF_REVENUE:
            'The currency of the vendor bill. It does not change the percent: the percent applies to the revenue of the billing service, in that revenue currency. The provision is converted to this currency at the day rate, so it matches the bill and is remeasured at month end. E.g. 40% of R$ 10,000.00 = R$ 4,000.00 → US$ 800.00 at 5.00. A vendor that bills in reais: keep Real. It is also the currency of the tolerance amount.',
          FIXED_PER_CONTRACT:
            "The currency the vendor charges in. The fixed amount beside it is written in this currency; the provision posts in the entity's functional currency at the day rate. It is also the currency of the tolerance amount.",
          FIXED_PER_MONTH:
            "The currency the vendor charges in. The fixed amount per month beside it is written in this currency; each month's provision posts in the entity's functional currency at the day rate. It is also the currency of the tolerance amount.",
        },
        costAccount:
          "Debit of the cost recognised in the month: each provision posts Dr this account / Cr the liability account. Closing a provision that will not come credits this account. The P&L line follows this account's mapping in FP&A > Rollups. Without it, provisions wait to be posted.",
        liabilityAccount:
          "Credit of the provision: each provision posts Cr this account. When the vendor's bill is approved, the relief debits this account against Accounts payable. A part paid ahead of its month is debited to Prepaid expenses (system account AP_COST_SERVICE_PREPAID) and reclassified against this account when that month's provision is created. It cannot be the accounts payable control account. Without it, provisions wait to be posted.",
        varianceAccount:
          'Difference at relief: when the bill does not match the relieved provision, the difference goes to this account (debit when the bill is higher, credit when it is lower). FX differences do not go here. If blank, approving a bill with a difference is refused.',
        activeFrom:
          'First day on which revenue creates a provision of this cost. Blank means no start limit.',
        activeTo:
          'Last day on which revenue creates a provision of this cost. Blank means no end. It cannot be before the start.',
        triggers:
          'The provision is born from revenue: each trigger is a moment when revenue is posted. Schedule: the monthly recognition of a contract item that has a schedule. Invoice issued: the revenue recognised when the invoice is issued (items without a schedule). Settlement approved: the revenue recognised when the settlement is approved (cash basis or acquirer). The entry is the same for all three (Dr cost account / Cr provision account); revenue posts once, so the cost does too. Untick a trigger and the revenue recognised at that moment gets no provision. To activate, tick at least one.',
        overrides:
          'How many linked billing services use their own rate instead of the rate of this service. Change it on the "Billing services" tab.',
        tolerance:
          'Difference accepted between the bill line and the provision without asking for confirmation. Exact: any difference calls for review. Amount: up to that amount, in the service currency (the same as the provision and the bill). Percent: up to that percent of the line amount. The difference goes to the variance account.',
        autoSelectEntriesWithinTolerance:
          'On: when the bill arrives within the tolerance, the provisions it pays come already selected for relief. Off: they are only suggested.',
        warnOnExpenseWithOpenProvisions:
          'On: approving a bill from this vendor with an expense line while it has open provisions writes a warning to the history. It never blocks the approval.',
        staleAfterDays:
          'Days after the expected payment date after which a provision still open shows in the aging. Blank means it never goes stale.',
      },
    },
    knob: {
      rateLabel: {
        PERCENT_OF_REVENUE: 'Cost rate (% of revenue)',
        FIXED_PER_CONTRACT: 'Cost amount per contract',
        FIXED_PER_MONTH: 'Cost amount per month',
      },
      currencyLabel: {
        PERCENT_OF_REVENUE: 'Vendor bill currency',
        FIXED_PER_CONTRACT: 'Vendor currency',
        FIXED_PER_MONTH: 'Vendor currency',
      },
      chargeFixedOnRenewalLabel: 'Charge again on renewal',
      derived: 'Calculated from the links',
      overridesSummary: 'Links with their own rate: {{count}}.',
      rateHint: {
        PERCENT_OF_REVENUE: 'Percent of the billed revenue. E.g. 12.5 or 12.5%',
        FIXED_PER_CONTRACT: 'Fixed amount per contract, in the vendor currency. E.g. 1,200.00',
        FIXED_PER_MONTH:
          'Fixed amount per month, per contract item, in the vendor currency. E.g. 100.00',
      },
      ratePlaceholder: {
        PERCENT_OF_REVENUE: '12.5%',
        FIXED_PER_CONTRACT: '1,200.00',
        FIXED_PER_MONTH: '100.00',
      },
      triggersHint:
        'All triggers make the same entry (Dr cost account / Cr provision account), only at different moments. Untick one and the revenue recognised at that moment gets no provision.',
      tolerance: {
        mode: {
          EXACT: 'Exact',
          AMOUNT: 'Amount',
          PERCENT: 'Percent',
        },
        amountLabel: 'Tolerance amount ({{currency}})',
        percentLabel: 'Tolerance percent',
      },
      fixedRecognition: {
        atOnce: 'At once',
        spread: 'Spread over N months',
        monthsLabel: 'Number of months',
        monthsSuffix: 'months (1 to 120)',
      },
      selectEntityFirst: 'Select the entity to choose the accounts.',
      selectAccount: 'Select the account',
      selectVendor: 'Select the vendor',
      selectCurrency: 'Select the currency',
      selectBaseType: 'Select the cost base',
      rateLockedHint_one:
        'The rate cannot change here: {{count}} link that uses this rate already has posted or pending provisions. Change the rate on the link ("Edit" under Billing services) and give the date it starts.',
      rateLockedHint_other:
        'The rate cannot change here: {{count}} links that use this rate already have posted or pending provisions. Change the rate on each link ("Edit" under Billing services) and give the date it starts.',
      baseTypeLockedHint_one:
        'The base cannot change while {{count}} live link has its own rate. End that link to change the base.',
      baseTypeLockedHint_other:
        'The base cannot change while {{count}} live links have their own rate. End those links to change the base.',
    },
    // "Cost summary": the sidebar of the form. The fixed-base wording lives in `fixed` (per
    // contract, D22/D40) and `monthly` (per month, D39).
    summary: {
      title: 'Cost summary',
      fields: {
        base: 'Base',
        rate: 'Rate',
        amount: 'Amount',
        currency: 'Currency',
        vendor: 'Vendor',
        triggers: 'Triggers',
        tolerance: 'Tolerance',
      },
      values: {
        notDefined: 'Not set',
        vendorChosen: 'Vendor chosen',
        noTriggers: 'None',
        noTriggersShort: 'no trigger',
        toleranceExact: 'Exact',
      },
      accounts: {
        cost: 'Cost account',
        liability: 'Provision account',
        variance: 'Variance account',
      },
      example: {
        title: 'Example',
        percent: 'Revenue of {{revenue}} → provision of {{provision}}',
        percentConverted:
          'Revenue of {{revenue}} → provision of {{provision}}, converted to {{currency}} at the day rate',
        needRate: 'Enter the rate to see an example.',
      },
      fixed: {
        field: 'Fixed amount recognition',
        atOnce: 'At once',
        spread_one: 'Over {{count}} month',
        spread_other: 'Over {{count}} months',
        spreadPending: 'In monthly parts (enter the months)',
        exampleAtOnce: '1st revenue of the contract → provision of {{amount}}',
        exampleSpread_one: '1st revenue of the contract → {{amount}} a month for {{count}} month',
        exampleSpread_other:
          '1st revenue of the contract → {{amount}} a month for {{count}} months',
        exampleSpreadRounded_one:
          '1st revenue of the contract → {{amount}} a month for {{count}} month; the last part takes the rounding',
        exampleSpreadRounded_other:
          '1st revenue of the contract → {{amount}} a month for {{count}} months; the last part takes the rounding',
        examplePending: 'Enter the number of months to see the example.',
        note: 'One amount per contract, provisioned once.',
        noteRenewal: 'One amount per contract, charged again on each contract renewal.',
      },
      monthly: {
        field: 'Fixed amount charge',
        charge: '{{amount}} a month per contract item',
        example:
          'Each month with revenue → provision of {{amount}} per contract item, whatever the contract price',
        note: 'One amount per contract item in each month with revenue, also after the renewal.',
      },
      sequence: {
        title: 'Cost sequence',
        revenue: 'Revenue recognised ({{triggers}})',
        revenueDetail: 'Provision: Dr {{cost}} / Cr {{liability}}',
        bill: "Vendor's bill arrives",
        billDetail: 'Relieves the provision.',
        difference: 'Difference at relief',
        differenceDetail: 'Within the tolerance: goes to {{variance}}. Outside: left for review.',
        differenceExact: 'Exact tolerance: any difference is left for review.',
      },
    },
    leaveDialog: {
      title: 'Leave without saving?',
      description: 'This cost service has unsaved changes. If you leave now they will be lost.',
      confirm: 'Leave without saving',
      stay: 'Keep editing',
    },
    result: {
      fields: {
        vendor: 'Vendor',
        rate: 'Rate',
        billingServices: 'Billing services',
        costService: 'Cost service',
        billingService: 'Billing service',
        period: 'Period',
      },
      rate: {
        PERCENT_OF_REVENUE: '{{rate}} of revenue',
        FIXED_PER_CONTRACT: '{{amount}} per contract',
        FIXED_PER_MONTH: '{{amount}} per month',
      },
      noBillingServices: 'None',
      activated: {
        nextWithLinks:
          'Provisions start at the next revenue recognition of these billing services.',
        nextWithoutLinks:
          'No billing service linked yet: link one in the "Billing services" tab to start provisioning.',
      },
      deactivated: {
        next: 'The service stops applying to the linked billing services. You can activate it again later.',
      },
      linked: {
        next: 'The cost is provisioned when the billing service revenue is recognized, while the cost service is active.',
      },
      edited: {
        next: 'The change applies from now on: the costs this link already recognized are not changed.',
      },
      ended: {
        next: 'The link history is kept and nothing is deleted.',
      },
    },
    links: {
      columns: {
        rate: 'Rate',
        period: 'Period',
        status: 'Status',
        actions: 'Actions',
      },
      inheritsRate: 'Service rate',
      overrideRate: 'own rate',
      period: '{{from}} – {{to}}',
      openEnded: 'No end',
      noStart: 'From the start',
      ended: 'Ended',
      edit: 'Edit',
      editNamed: 'Edit: {{name}}',
      end: 'End',
      endNamed: 'End: {{name}}',
      open: 'Open',
      openNamed: 'Open: {{name}}',
      unknownName: 'Removed service',
      nameUnavailable: 'Service name unavailable',
      loadError: 'Could not load the links.',
      noPermission: 'You do not have permission to view cost services.',
      terms: {
        rateOverride: 'Own rate (optional)',
        activeFrom: 'Active from',
        activeTo: 'Active until',
      },
      addDialog: {
        rateAfterService: 'Pick the cost service to enter its own rate.',
        submit: 'Link',
        cancel: 'Cancel',
      },
      editDialog: {
        title: 'Edit the link with "{{name}}"',
        description:
          'Change the own rate or the period. The change applies from now on: the costs this link already recognised are not altered.',
        ended: 'This link has ended. To reopen it, clear the end date or pick a future date.',
        confirm: 'Save link',
        cancel: 'Cancel',
        unchanged: 'Change the rate or the period to save.',
        success: 'Link updated.',
        effectiveFrom: 'New rate applies from',
        effectiveFromHint:
          'If the link already provisioned costs, it ends the day before this date and a new link carries the new rate; earlier months, even when posted later, keep the old rate.',
        effectiveFromRequired: 'Say from when the new rate applies.',
        earliestAfterPosted:
          'Provisions are already posted or pending through {{through}}. Choose a date from {{date}} on. To fix posted months, use reversals and manual journals.',
        earliestAfterClosed:
          'The period before {{date}} is closed. Choose a date from {{date}} on.',
        earliestUnavailable:
          'The earliest date for the new rate could not be read. Close and reopen the dialog.',
      },
      endDialog: {
        title: 'End the link with "{{name}}"?',
        description:
          'The link stops applying from the date below. The history is kept and nothing is deleted.',
        endDate: 'Last day of the link',
        confirm: 'End link',
        success: 'Link ended.',
      },
      costService: {
        title: 'Linked billing services',
        description:
          'Each link says which billing services this cost follows, with its own rate and period if needed.',
        columnName: 'Billing service',
        empty: {
          title: 'No billing service linked',
          description: 'Link a billing service so this cost follows its revenue.',
        },
        add: 'Link service',
        loadNamesError: 'Could not load the billing services of this entity.',
        addDialog: {
          title: 'Link billing service',
          description: 'Only active billing services of the same entity as this cost are listed.',
          service: 'Billing service',
          servicePlaceholder: 'Select the billing service',
          noServices: 'This entity has no active billing services.',
          serviceRequired: 'Select the billing service.',
          success: 'Billing service linked.',
        },
      },
      billingService: {
        title: 'Cost services',
        description:
          'Cost services that follow this billing service, with their own rate and period if needed.',
        columnName: 'Cost service',
        empty: {
          title: 'No cost service linked',
          description: 'Link a cost service so it follows the revenue of this billing service.',
        },
        add: 'Link cost',
        loadNamesError: 'Could not load the cost services of this entity.',
        addDialog: {
          title: 'Link cost service',
          description:
            'Only cost services of the same entity as this billing service are listed, without the inactive ones.',
          service: 'Cost service',
          servicePlaceholder: 'Select the cost service',
          noServices: 'This entity has no cost service to link.',
          serviceRequired: 'Select the cost service.',
          success: 'Cost service linked.',
        },
      },
    },
    errors: {
      generic: 'Could not complete the operation. Check the data and try again.',
    },
  },
};
